import './Faqs.scss';
import SupportPageHelpSection from '../../components/HowToSync/SupportPageHelpSection';
import { useTranslation } from 'react-i18next';
import { faqsList } from './FaqsList';
import FaqsSection from '../../components/Faqs/FaqsSection';
import ReactDOMServer from 'react-dom/server';
import {
  Autocomplete,
  Button,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { faqAutocomplete } from '../../assets/style/material/matSyles';
import {
  IFaq,
  IFaqsSection,
  IFaqsSubsection
} from '../../models/FaqsInterface';
import AcBackToTopButton from '../../components/AcBackToTopButton/AcBackToTopButton';
import React from 'react';
import {
  checkForAlphabeticChars,
  replaceSpecialChars
} from '../../helpers/commonHelpers';

interface IFaqDec extends IFaq {
  dec_ques: string;
  key: string;
  isAnswer?: boolean;
}

const isHtmlString = (str: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  return Array.from(doc.body.childNodes).some(
    (node) => node.nodeType === Node.ELEMENT_NODE
  );
};

function textHighlighter(text: string, regex: RegExp): string {
  return text.replace(regex, (match) => `<b>${match}</b>`) || '';
}

const generateHighlightedAnswer = (node: Node, regex: RegExp): Node => {
  if (node.nodeType === Node.TEXT_NODE) {
    const element = document.createElement('span');
    element.innerHTML = textHighlighter(node.nodeValue || '', regex);
    const fragment = document.createDocumentFragment();
    Array.from(element.childNodes).forEach((child) =>
      fragment.appendChild(child)
    );
    return fragment;
  } else {
    if (node.hasChildNodes()) {
      const children = Array.from(node.childNodes);
      children.forEach((child) => {
        const updatedChildren = generateHighlightedAnswer(child, regex);
        if (updatedChildren) {
          node.replaceChild(updatedChildren, child);
        }
      });
    }
    return node;
  }
};

const Faqs = () => {
  const { t } = useTranslation();

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const autocompleteStyle = faqAutocomplete();
  const [autocompleteText, setAutocompleteText] = useState('');
  const filteredFaqList = useRef<IFaqsSection[]>([]);
  const [displayList, setDisplayList] = useState(faqsList);
  const [searching, setSearching] = useState(false);
  const [options, setOptions] = useState<IFaq[]>([]);
  const [reset, setReset] = useState(false);
  const [selectedFaqs, setSelectedFaqs] = useState<boolean | null>(null);

  useEffect(() => {
    if (!searching) {
      setDisplayList(faqsList);
    }
    // eslint-disable-next-line
  }, [searching]);

  useEffect(() => {
    setReset(false);
  }, [selectedFaqs]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.slice(1) === 'sdo') {
      let id = '';
      displayList.some((section, secInd) =>
        section.subsections.some((subsection, subSecInd) => {
          if (subsection.title.includes('SDO')) {
            id = `sec-${secInd}-subsec-${subSecInd}`;
            return true;
          }
          return false;
        })
      );
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line
  }, []);

  function renderOption(props, option) {
    return (
      <Typography
        {...props}
        key={option.key}
        style={{ fontSize: '1.4rem', display: 'block' }}
        dangerouslySetInnerHTML={{ __html: option.dec_ques }}
      ></Typography>
    );
  }

  function makeFilteredList(
    condition: (
      faq: IFaq,
      secInd: number,
      subSecInd: number,
      faqInd
    ) => boolean | IFaq,
    sourceList: IFaqsSection[] = faqsList
  ) {
    const filteredList: IFaqsSection[] = [];

    sourceList.forEach((section, secInd) => {
      section.subsections.forEach((subsection, subSecInd) => {
        subsection.faqs.forEach((faq, faqInd) => {
          const fq = condition(faq, secInd, subSecInd, faqInd);
          if (fq) {
            let sec = filteredList.find((s) => s.id === section.id);
            if (!sec) {
              sec = { ...section, subsections: [] };
              filteredList.push(sec);
            }
            let subSec = sec.subsections.find((s) => s.id === subsection.id);
            if (!subSec) {
              subSec = { ...subsection, faqs: [] };
              sec.subsections.push(subSec);
            }
            let f = subSec.faqs.find((f) => f.id === faq.id);
            if (!f) {
              f = typeof fq === 'boolean' ? { ...faq } : { ...fq };
              subSec.faqs.push(f);
            }
          }
        });
      });
    });
    return filteredList;
  }

  function handleAutocompleteOnChange(
    event: SyntheticEvent<Element, Event>,
    value
  ): void {
    setSelectedFaqs(true);
    filteredFaqList.current = makeFilteredList(
      (faq) => faq.id === value.id,
      filteredFaqList.current
    );
    setAutocompleteText(value.question);
    setDisplayList(filteredFaqList.current);
  }

  const onChange = (e) => {
    const newValue = e.target.value;
    setSelectedFaqs(null);
    if (!newValue) {
      setAutocompleteText('');
      setDisplayList(faqsList);
    }
    // eslint-disable-next-line
    else if (checkForAlphabeticChars(newValue)) {
      setAutocompleteText(newValue);
      if (newValue.length >= 3) {
        searchFAQs(newValue.trim());
      }
    }
  };

  const prepositions = [
    'in',
    'at',
    'on',
    'or',
    'and',
    'with',
    'a',
    'the',
    'for',
    'etc'
  ];

  function searchFAQs(query: string) {
    const words = query
      .split(/\s+/)
      .filter(
        (word) => word.length >= 3 && !prepositions.includes(word.toLowerCase())
      );
    if (words.length === 0) {
      setOptions([]);
      return;
    }
    const regexPattern = new RegExp(`\\b(${words.join('|')})\\b`, 'gi');
    const options_questions: IFaqDec[] = [];
    const options_answers: IFaqDec[] = [];
    let id = '0';

    filteredFaqList.current = makeFilteredList(
      (faq: IFaq, section_id, subsection_id, faqInd) => {
        const ques_match = regexPattern.test(faq.question);
        id = Number(id) + 1 + '';
        let dec_Faq: IFaq = { ...faq, id };
        if (ques_match) {
          options_questions.push({
            ...faq,
            key: `${section_id}-${subsection_id}-${faqInd}-0`,
            dec_ques: faq.question.replace(
              new RegExp(regexPattern),
              (match) => `<b>${match}</b>`
            ),
            id
          });
          dec_Faq.question = faq.question.replace(
            new RegExp(regexPattern),
            (match) => `<b>${match}</b>`
          );
        }
        regexPattern.lastIndex = 0;
        let string = '';
        let highlightedAnswer = '';
        if (typeof faq.answer === 'string') {
          const doc = new DOMParser().parseFromString(faq.answer, 'text/html');
          string = doc.body.textContent || '';
          if (isHtmlString(faq.answer)) {
            generateHighlightedAnswer(doc.body, new RegExp(regexPattern));
            highlightedAnswer = doc.body.innerHTML;
          } else {
            highlightedAnswer = textHighlighter(string, regexPattern);
          }
        } else if (typeof faq.answer === typeof React.Component) {
          const doc = new DOMParser().parseFromString(
            ReactDOMServer.renderToString(<faq.answer />),
            'text/html'
          );
          string = doc.body.textContent || '';
        }
        const ans_match = regexPattern.test(string);
        if (ans_match) {
          regexPattern.lastIndex = 0;
          dec_Faq.answer = highlightedAnswer;
          let match;
          let flag = true;
          let start = 0;
          const MAX_LENGTH = 30;
          let counter = 1;
          while ((match = regexPattern.exec(string)) !== null) {
            if (flag) {
              flag = false;
              start = match.index;
            }
            if (match.index - start > MAX_LENGTH) {
              const beforeMatch = string
                .slice(0, start)
                .split(/\s+/)
                .slice(-2)
                .join(' ');
              const afterMatch = string
                .slice(start + MAX_LENGTH)
                .split(/\s+/)
                .slice(0, 2)
                .join(' ');
              const partial_string = `${beforeMatch}${string.slice(start, start + MAX_LENGTH)}${afterMatch}`;

              options_answers.push({
                ...faq,
                key: `${section_id}-${subsection_id}-${faqInd}-${counter++}`,
                dec_ques:
                  `${faq.question}  ...${partial_string.replace(new RegExp(regexPattern), (match) => `<b>${match}</b>`)}...`.trim(),
                isAnswer: true,
                id: id
              });
              start = match.index;
            }
          }
          if (start !== 0) {
            const beforeMatch = string
              .slice(0, start)
              .split(/\s+/)
              .slice(-3)
              .join(' ');
            const partial_string = `${beforeMatch}${string.slice(start, start + MAX_LENGTH)}`;
            options_answers.push({
              ...faq,
              key: `${section_id}-${subsection_id}-${faqInd}-${counter}`,
              dec_ques:
                `${faq.question}  ...${partial_string.replace(new RegExp(regexPattern), (match) => `<b>${match}</b>`)}...`.trim(),
              isAnswer: true,
              id: id
            });
          }
        }
        return ques_match || ans_match ? dec_Faq : false;
      }
    );
    setOptions([...options_questions, ...options_answers]);
  }

  function onSearch() {
    if (autocompleteText.length === 0 && filteredFaqList.current.length === 0) {
      setSearching(false);
      return;
    }
    setSearching(true);
    setSelectedFaqs(null);
    setAutocompleteOpen(false);
    setDisplayList(filteredFaqList.current);
  }

  function onClear() {
    setAutocompleteText('');
    setSelectedFaqs(null);
    filteredFaqList.current = [];
    setDisplayList(faqsList);
    setReset(true);
    setSearching(false);
  }

  const JumpToSection = () => {
    // Creating a list of objects containing id and title of every section and topic to be displayed in jump to section.
    const list: { id: string; title: string }[] = faqsList.reduce(
      (list: { id: string; title: string }[], section: IFaqsSection, secInd) =>
        section.subsections.length === 0
          ? list
          : [
              {
                id: `sec-${secInd}`,
                title: section.title
              },
              ...list,
              ...section.subsections.reduce(
                (
                  list: { id: string; title: string }[],
                  subsection: IFaqsSubsection,
                  subSecInd
                ) =>
                  subsection.faqs.length === 0
                    ? list
                    : [
                        ...list,
                        {
                          id: `sec-${secInd}-subsec-${subSecInd}`,
                          title: subsection.title
                        }
                      ],
                []
              )
            ],
      []
    );

    function filterSections(id: string) {
      const [secInd, , subSecInd] = id.split('-').slice(1).map(Number);
      filteredFaqList.current = makeFilteredList(
        (_, sec, subSec) =>
          sec === secInd && (subSecInd === undefined || subSec === subSecInd)
      );
      setSearching(false);
      setSelectedFaqs(null);
      setAutocompleteOpen(false);
      setAutocompleteText('');
      setDisplayList(filteredFaqList.current);
    }

    return list.length !== 0 ? (
      <div className="jump-to-section">
        <h2 className="jump-to-label">{t('faqs.jumpTo')}</h2>
        <div className="jump-to-links">
          <Grid container rowGap={2}>
            {list.map((section) => (
              <Grid item key={section.id} minWidth={'25%'}>
                <p
                  className="jump-to-link"
                  onClick={() => filterSections(section.id)}
                >
                  {section.title}
                </p>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
    ) : (
      <></>
    );
  };

  return (
    <div className="faq-container">
      <div className="faqs-header-container">
        <div className="faqs-header">
          <div className="faqs-title">{t('faqs.title')}</div>
        </div>
      </div>
      <div></div>
      <div className="faqs-body">
        <AcBackToTopButton />
        <div className="faqs-left-section">
          <div className="search-bar">
            <Autocomplete
              open={autocompleteOpen}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  const newValue = replaceSpecialChars(value);
                  if (newValue.trim().length < 3) {
                    if (autocompleteOpen) setAutocompleteOpen(false);
                  } else {
                    if (!autocompleteOpen) setAutocompleteOpen(true);
                  }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onSearch();
                }
              }}
              filterOptions={(x) => x}
              autoComplete
              getOptionLabel={(option) => option.question}
              inputValue={autocompleteText}
              onClose={() => {
                setAutocompleteOpen(false);
              }}
              className={autocompleteStyle.autocomplete}
              onChange={(event, value) =>
                handleAutocompleteOnChange(event, value)
              }
              options={options}
              noOptionsText={
                <Typography
                  style={{
                    fontSize: '1.4rem',
                    display: 'block',
                    color: 'black'
                  }}
                >
                  {t('faqs.noOptionsText')}
                </Typography>
              }
              slotProps={{ popper: { className: 'autocomplete-popper' } }}
              popupIcon={null}
              value={null}
              renderOption={(props, option) => renderOption(props, option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className={autocompleteStyle.textField}
                  label="Search for a keyword or phrase"
                  onChange={onChange}
                  InputLabelProps={{
                    className: autocompleteStyle.textFieldLabel
                  }}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    inputProps: {
                      ...params.inputProps,
                      maxLength: 100,
                      style: { paddingLeft: 0 }
                    }
                  }}
                />
              )}
            />
            <div className="search-buttons">
              <Button
                variant="outlined"
                onClick={onSearch}
                className="search-btn"
              >
                {t('faqs.searchButton')}
              </Button>
              <Button
                variant="outlined"
                onClick={onClear}
                className="cancel-btn"
              >
                {t('faqs.clearButton')}
              </Button>
            </div>
          </div>
          {searching &&
            displayList.length === 0 &&
            autocompleteText.length > 0 && (
              <div className="no-search-container">
                <i className="icon-circle-flight"></i>
                <p>
                  {t('faqs.noSearchMessage', { keyword: autocompleteText })}
                </p>
              </div>
            )}

          {/* Jump to section component */}
          <JumpToSection />
          {/* render list of sections */}
          {displayList &&
            displayList.length > 0 &&
            displayList.map((section, secInd) => (
              <FaqsSection
                key={section.id}
                id={secInd}
                title={section.title}
                subsections={section.subsections}
                selectedFaqs={selectedFaqs}
                reset={reset}
                setReset={setReset}
              />
            ))}
        </div>
        <div className="faqs-right-section">
          <SupportPageHelpSection />
        </div>
      </div>
    </div>
  );
};

export default Faqs;
